// AGC: LotBOMCard - Bill of Materials card for lot inputs
// AGC: REDESIGNED v2: Uses unified SupplyCard components, sorted by priority
// AGC: Shows required inputs vs current inventory for a lot

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { 
  Package, 
  AlertTriangle, 
  X, 
  ChevronDown, 
  ChevronUp,
  RefreshCw,
  ExternalLink,
  Share2,
  ArrowUpCircle,
  Camera,
  Loader2,
  ArrowUpDown,
  Filter,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useInventory } from '../../hooks/useInventory'
import { useAuth } from '../../hooks/useAuth'
import {
  calculateLotBOM,
  fetchTaskTemplates,
  getBOMSummary,
  type LotInfo,
} from '../../services/bomService'
import { getBackendUrl } from '../../utils/backendUrl'
import { shareQuote, filterBOMItems, copyQuoteToClipboard } from '../../services/quotePdfService'
import type { QuoteData, QuoteFilterType, QuoteTranslations } from '../../services/quotePdfService'
import { track } from '../../lib/analytics'
import type { LotBOM, BOMItem, StockStatus, InputCategory } from '../../types/inventory'
import { AddStockModal, type StockEntryItem } from './AddStockModal'
import { ScanInvoiceModal } from './ScanInvoiceModal'
import { SupplyCard, SupplyDetailModal } from '../supplies'
import toast from 'react-hot-toast'

// AGC: Sort order for stock status (most urgent first)
const STOCK_STATUS_PRIORITY: Record<StockStatus, number> = {
  out: 0,        // AGC: Sin stock = máxima urgencia
  critical: 1,   // AGC: Crítico = muy urgente
  low: 2,        // AGC: Bajo = urgente
  ok: 3,         // AGC: OK = normal
  overstock: 4,  // AGC: Sobrestock = menos urgente
}

// AGC: Sort type options
type SortType = 'priority' | 'name' | 'category'
type FilterType = 'all' | 'missing' | 'low' | 'ok'

interface LotBOMCardProps {
  lotId: string
  lotName: string
  areaHa: number
  cropId: string
  cropName: string
  farmName?: string
  farmLocation?: { lat: number; lng: number }
  allLots?: Array<{ id: string; name: string; cropName: string }>
  onNavigateToInventory?: () => void
}

/**
 * AGC: LotBOMCard - Shows BOM for lot inputs with stock status
 * AGC: Redesigned with minimalist inline action buttons below summary
 */
export const LotBOMCard: React.FC<LotBOMCardProps> = ({
  lotId,
  lotName,
  areaHa,
  cropId,
  cropName,
  farmName = 'Minha Fazenda',
  farmLocation,
  allLots = [],
  onNavigateToInventory,
}) => {
  const { t } = useTranslation()
  const { items: inventoryItems, loading: inventoryLoading, recordMovement, addItem, ensureItemExists } = useInventory()
  const { getAuthToken, user } = useAuth()
  
  const [bom, setBom] = useState<LotBOM | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  
  // AGC: Modal states
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false)
  const [isScanInvoiceModalOpen, setIsScanInvoiceModalOpen] = useState(false)
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false)
  const [isGeneratingQuote, setIsGeneratingQuote] = useState(false)
  const shareMenuRef = useRef<HTMLDivElement>(null)
  
  // AGC: Supply detail modal state
  const [selectedSupply, setSelectedSupply] = useState<BOMItem | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  
  // AGC: Sort & filter states
  const [sortType, setSortType] = useState<SortType>('priority')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const sortMenuRef = useRef<HTMLDivElement>(null)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  
  // AGC: Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setIsShareMenuOpen(false)
      }
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setIsSortMenuOpen(false)
      }
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setIsFilterMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // AGC: Sorted and filtered items
  const sortedAndFilteredItems = useMemo(() => {
    if (!bom) return []
    
    let items = [...bom.items]
    
    // AGC: Apply filter
    switch (filterType) {
      case 'missing':
        items = items.filter(i => i.stockStatus === 'out' || i.stockStatus === 'critical')
        break
      case 'low':
        items = items.filter(i => i.stockStatus === 'low')
        break
      case 'ok':
        items = items.filter(i => i.stockStatus === 'ok' || i.stockStatus === 'overstock')
        break
      default:
        break
    }
    
    // AGC: Apply sort
    switch (sortType) {
      case 'priority':
        items.sort((a, b) => STOCK_STATUS_PRIORITY[a.stockStatus] - STOCK_STATUS_PRIORITY[b.stockStatus])
        break
      case 'name':
        items.sort((a, b) => a.inputName.localeCompare(b.inputName))
        break
      case 'category':
        items.sort((a, b) => a.category.localeCompare(b.category))
        break
    }
    
    return items
  }, [bom, sortType, filterType])
  
  // AGC: Handle supply card click - open detail modal
  const handleSupplyClick = useCallback((item: BOMItem) => {
    setSelectedSupply(item)
    setIsDetailModalOpen(true)
  }, [])
  
  // AGC: Handle stock entry from AddStockModal - auto-creates inventory items if they don't exist
  const handleStockEntry = async (entries: StockEntryItem[]) => {
    let successCount = 0
    let createdCount = 0
    
    try {
      for (const entry of entries) {
        if (entry.quantityToAdd > 0) {
          let existingItem = inventoryItems.find(i => 
            i.name.toLowerCase() === entry.inputName.toLowerCase() ||
            i.inputId === entry.inputId
          )
          
          // AGC: If item doesn't exist in inventory, create it first
          if (!existingItem && addItem) {
            console.log(`🆕 AGC: Creating new inventory item "${entry.inputName}" (not found in inventory)`)
            try {
              const newItem = await addItem({
                userId: '', // AGC: Filled by backend via auth token
                farmId: '', // AGC: Filled by backend via auth token
                name: entry.inputName,
                inputId: entry.inputId,
                category: entry.category || 'other',
                quantity: 0,  // AGC: Will be updated by the movement
                unit: entry.unit,
                minimumStock: 0,
              })
              existingItem = newItem
              createdCount++
              console.log(`✅ AGC: Created inventory item "${entry.inputName}" with id ${newItem.id}`)
            } catch (createError) {
              console.error(`❌ AGC: Failed to create inventory item "${entry.inputName}":`, createError)
              continue
            }
          }
          
          // AGC: Now record the stock movement
          if (existingItem && recordMovement) {
            console.log(`📦 AGC: Adding ${entry.quantityToAdd} ${entry.unit} of ${entry.inputName} to inventory item ${existingItem.id}`)
            await recordMovement(existingItem.id, {
              type: 'entry',
              quantity: entry.quantityToAdd,
              reason: t('lotPage.stockEntryReason', { lotName }),
            })
            successCount++
          }
        }
      }
      
      await calculateBOM()
      
      if (successCount > 0) {
        if (createdCount > 0) {
          toast.success(t('lotPage.stockAddSuccess', { count: successCount, created: createdCount }))
        } else {
          toast.success(t('lotPage.entrySuccess'))
        }
      } else {
        toast.error(t('inventory.errors.noItemsAdded'))
      }
    } catch (error) {
      console.error('AGC: Error adding stock entries:', error)
      toast.error(t('inventory.errors.addStockFailed'))
      throw error
    }
  }
  
  // AGC: Refs to break the inventoryItems → calculateBOM → ensureItemExists → inventoryItems loop.
  // AGC: Before: inventoryItems was a useCallback dep, ensureItemExists wrote to inventory,
  // AGC: which re-rendered useInventory, which gave a new inventoryItems ref, which rebuilt
  // AGC: calculateBOM, which re-ran the useEffect, which fetched /task-templates again — ~20/sec loop.
  // AGC: Fix: read inventoryItems + ensureItemExists through refs, and only auto-sync ONCE per (lotId, cropId).
  const inventoryItemsRef = useRef(inventoryItems)
  useEffect(() => { inventoryItemsRef.current = inventoryItems }, [inventoryItems])
  const ensureItemExistsRef = useRef(ensureItemExists)
  useEffect(() => { ensureItemExistsRef.current = ensureItemExists }, [ensureItemExists])
  const lastSyncedKey = useRef<string | null>(null)

  // AGC: Calculate BOM via backend — same endpoint WhatsApp agent uses (calculateBOMForLot).
  // AGC: Before v3.13.7 this did client-side fetchTaskTemplates + calculateLotBOM which diverged
  // AGC: from the WhatsApp "insumos necessarios para talhao X" response. Now both use the same
  // AGC: server-side function so the numbers match exactly.
  const calculateBOM = useCallback(async () => {
    if (!lotId || areaHa <= 0) {
      setBom(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await getAuthToken()
      const res = await fetch(`${getBackendUrl()}/api/v1/inventory/lots/${lotId}/bom?areaHa=${areaHa}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-user-id': user?.uid || '',
        },
      })
      if (!res.ok) {
        throw new Error(`Backend BOM fetch failed: ${res.status}`)
      }
      const json = await res.json()
      if (!json.success || !json.data) {
        throw new Error(json.error || 'BOM response missing data')
      }

      // AGC: Backend returns BOM in the same shape calculateBOMForLot produces.
      // AGC: Decorate with lot/crop metadata the UI expects.
      const serverBom = json.data
      setBom({
        lotId,
        lotName,
        areaHa,
        cropId,
        cropName,
        items: serverBom.items || [],
        totalEstimatedCost: serverBom.totalEstimatedCost || 0,
        itemsInStock: serverBom.itemsInStock || 0,
        itemsLow: serverBom.itemsLow || 0,
        itemsOut: serverBom.itemsOut || 0,
        generatedAt: serverBom.generatedAt || new Date().toISOString(),
      })

      // AGC: Auto-sync BOM items to local inventory — ONCE per (lotId, cropId).
      const syncKey = `${lotId}:${cropId}`
      const ensureFn = ensureItemExistsRef.current
      const items = serverBom.items || []
      if (items.length > 0 && ensureFn && lastSyncedKey.current !== syncKey) {
        lastSyncedKey.current = syncKey
        for (const bomItem of items) {
          try {
            await ensureFn(
              bomItem.inputId,
              bomItem.inputName,
              bomItem.category as InputCategory,
              bomItem.unit,
              bomItem.requiredQuantity
            )
          } catch (syncError) {
            console.error(`❌ AGC: Failed to sync item ${bomItem.inputName}:`, syncError)
          }
        }
      }
    } catch (err: any) {
      console.error('AGC: Error fetching BOM from backend:', err)
      setError(err.message || 'Failed to load insumos')
    } finally {
      setLoading(false)
    }
  }, [lotId, lotName, areaHa, cropId, cropName, getAuthToken, user?.uid])

  // AGC: Auto-calculate BOM on mount and when lot/crop changes (NOT when inventoryItems changes — read via ref)
  useEffect(() => {
    calculateBOM()
  }, [calculateBOM])
  
  // AGC: Get summary stats
  const summary = bom ? getBOMSummary(bom) : null
  
  // AGC: Get translations for PDF
  const getQuoteTranslations = (): QuoteTranslations => ({
    quoteRequest: t('lotPage.quoteRequest'),
    quoteSubtitle: t('lotPage.quoteSubtitle'),
    farmLabel: t('lotPage.farmLabel'),
    locationLabel: t('lotPage.locationLabel'),
    dateLabel: t('lotPage.dateLabel'),
    generatedBy: t('lotPage.generatedBy'),
    itemColumn: t('lotPage.itemColumn'),
    qtyColumn: t('lotPage.qtyColumn'),
    unitColumn: t('lotPage.unitColumn'),
  })
  
  // AGC: Handle share quote
  const handleShareQuote = async (filterType: QuoteFilterType) => {
    if (!bom) return
    setIsShareMenuOpen(false)
    
    const filteredItems = filterBOMItems(bom.items, filterType)
    if (filteredItems.length === 0) {
      toast.error(t('lotPage.noItemsToShare'))
      return
    }
    
    setIsGeneratingQuote(true)
    const loadingToast = toast.loading(t('lotPage.pdfGenerating'))
    // AGC: Telemetry — PDF/quote generation triggered
    void track('feature_used', { feature_name: 'pdf_export' })

    try {
      const quoteData: QuoteData = {
        farmName,
        farmLocation,
        lotName,
        lotArea: areaHa,
        items: bom.items,
        date: new Date(),
        filterType,
      }
      
      await shareQuote(quoteData, getQuoteTranslations())
      toast.dismiss(loadingToast)
      toast.success(t('lotPage.pdfSuccess'))
    } catch (error) {
      toast.dismiss(loadingToast)
      console.error('AGC: Error sharing quote:', error)
      
      try {
        const quoteData: QuoteData = {
          farmName,
          farmLocation,
          lotName,
          lotArea: areaHa,
          items: bom.items,
          date: new Date(),
          filterType,
        }
        await copyQuoteToClipboard(quoteData, getQuoteTranslations())
        toast.success(t('inventory.quoteCopied'))
      } catch (copyError) {
        toast.error(t('lotPage.pdfError'))
      }
    } finally {
      setIsGeneratingQuote(false)
    }
  }
  
  // AGC: Count items by status for share menu
  const lowCount = bom?.items.filter(i => i.stockStatus === 'low' || i.stockStatus === 'critical').length || 0
  const outCount = bom?.items.filter(i => i.stockStatus === 'out').length || 0
  
  // AGC: Find inventory item for a BOM item
  const findInventoryItem = useCallback((item: BOMItem) => {
    return inventoryItems.find(i => 
      i.inputId === item.inputId || 
      i.name.toLowerCase() === item.inputName.toLowerCase()
    )
  }, [inventoryItems])
  
  // AGC: Loading state
  // AGC: Timeout guard — if loading hangs >10s, fall through to error/empty state instead of infinite spinner
  const [bomTimedOut, setBomTimedOut] = useState(false)
  useEffect(() => {
    const stillLoading = loading || inventoryLoading
    if (!stillLoading) { setBomTimedOut(false); return }
    const timer = setTimeout(() => setBomTimedOut(true), 10000)
    return () => clearTimeout(timer)
  }, [loading, inventoryLoading])

  if ((loading || inventoryLoading) && !bomTimedOut) {
    return (
      <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('lotPage.supplies')}
          </h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    )
  }
  
  // AGC: Error state
  if (error) {
    return (
      <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('common.error')}
          </h3>
        </div>
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={calculateBOM}
          className="mt-3 text-sm text-blue-600 hover:text-blue-700"
        >
          {t('common.retry')}
        </button>
      </div>
    )
  }
  
  // AGC: No BOM or no items
  if (!bom || bom.items.length === 0) {
    return (
      <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <Package className="w-4 h-4 text-gray-500" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('lotPage.supplies')}
            </h3>
          </div>
          <button
            onClick={calculateBOM}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title={t('common.refresh')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('lotPage.noSuppliesConfigured')}
        </p>
      </div>
    )
  }
  
  // AGC: Main render - NEW STRUCTURE
  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* AGC: Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('lotPage.supplies')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {bom.items.length} {t('lotPage.suppliesDescription')} {areaHa.toFixed(1)} ha
              </p>
            </div>
          </div>
          
          {onNavigateToInventory && (
            <button
              onClick={onNavigateToInventory}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('inventory.title')}
            >
              <ExternalLink className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
        
        {/* AGC: Progress bar (simplified summary) */}
        {summary && bom.items.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
              <span>{t('lotPage.suppliesProgress')}</span>
              <span>{summary.completeItems} / {bom.items.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${(summary.completeItems / bom.items.length) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {/* AGC: Action buttons row - Minimalist icon-only style */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {/* AGC: Share Quote Button with Dropdown */}
            <div className="relative" ref={shareMenuRef}>
              <button
                onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                disabled={isGeneratingQuote || bom.items.length === 0}
                className={`p-2 rounded-lg transition-colors ${
                  isGeneratingQuote || bom.items.length === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={t('lotPage.shareQuote')}
              >
                {isGeneratingQuote ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Share2 className="w-5 h-5" />
                )}
              </button>

              {/* AGC: Dropdown Menu */}
              {isShareMenuOpen && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                  <button
                    onClick={() => handleShareQuote('all')}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <Package className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-gray-700 dark:text-gray-300">{t('lotPage.shareAll')}</span>
                    <span className="ml-auto text-[10px] text-gray-400">{bom.items.length}</span>
                  </button>
                
                <button
                  onClick={() => handleShareQuote('low')}
                  disabled={lowCount === 0}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs ${
                    lowCount === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-gray-700 dark:text-gray-300">{t('lotPage.shareLow')}</span>
                  <span className="ml-auto text-[10px] text-gray-400">{lowCount}</span>
                </button>
                  
                  <button
                    onClick={() => handleShareQuote('out')}
                    disabled={outCount === 0}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs ${
                      outCount === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <X className="w-3.5 h-3.5 text-red-500" />
                    <span className="text-gray-700 dark:text-gray-300">{t('lotPage.shareOut')}</span>
                    <span className="ml-auto text-[10px] text-gray-400">{outCount}</span>
                  </button>
                </div>
              )}
            </div>

            {/* AGC: Add Stock Button - Icon only */}
            <button
              onClick={() => setIsAddStockModalOpen(true)}
              className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
              title={t('lotPage.addStockEntry')}
            >
              <ArrowUpCircle className="w-5 h-5" />
            </button>

            {/* AGC: Scan Invoice Button (Em Breve) - Icon only */}
            <button
              onClick={() => setIsScanInvoiceModalOpen(true)}
              className="relative p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('lotPage.scanInvoice')}
            >
              <Camera className="w-5 h-5" />
              <span className="absolute -top-0.5 -right-0.5 px-1 py-0.5 bg-purple-500 text-white text-[8px] font-bold rounded-full leading-none">
                {t('lotPage.comingSoon')}
              </span>
            </button>
          </div>
          
          {/* AGC: Cost estimate badge */}
          {bom.totalEstimatedCost > 0 && (
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              R$ {bom.totalEstimatedCost.toFixed(2)}
            </div>
          )}
        </div>
        
        {/* AGC: Expand/collapse button with sort/filter controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-1 flex items-center justify-center gap-2 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                {t('common.showLess')}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                {t('lotPage.viewDetails')} ({bom.items.length})
              </>
            )}
          </button>
          
          {/* AGC: Sort dropdown */}
          {isExpanded && (
            <>
              <div className="relative" ref={sortMenuRef}>
                <button
                  onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title={t('common.sort')}
                >
                  <ArrowUpDown className="w-4 h-4" />
                </button>
                
                {isSortMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    <button
                      onClick={() => { setSortType('priority'); setIsSortMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${sortType === 'priority' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.sortPriority')}
                    </button>
                    <button
                      onClick={() => { setSortType('name'); setIsSortMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${sortType === 'name' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.sortName')}
                    </button>
                    <button
                      onClick={() => { setSortType('category'); setIsSortMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${sortType === 'category' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.sortCategory')}
                    </button>
                  </div>
                )}
              </div>
              
              {/* AGC: Filter dropdown */}
              <div className="relative" ref={filterMenuRef}>
                <button
                  onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                  className={`p-2 rounded-lg transition-colors ${
                    filterType !== 'all' 
                      ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30' 
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={t('common.filter')}
                >
                  <Filter className="w-4 h-4" />
                </button>
                
                {isFilterMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    <button
                      onClick={() => { setFilterType('all'); setIsFilterMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${filterType === 'all' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.filterAll')}
                    </button>
                    <button
                      onClick={() => { setFilterType('missing'); setIsFilterMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${filterType === 'missing' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.filterMissing')}
                    </button>
                    <button
                      onClick={() => { setFilterType('low'); setIsFilterMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${filterType === 'low' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.filterLow')}
                    </button>
                    <button
                      onClick={() => { setFilterType('ok'); setIsFilterMenuOpen(false) }}
                      className={`w-full px-3 py-2 text-left text-xs ${filterType === 'ok' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      {t('profile.supplies.filterOk')}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* AGC: Expanded items list using SupplyCard */}
      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {sortedAndFilteredItems.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('profile.supplies.noItemsFilter')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedAndFilteredItems.map((item) => {
                const inventoryItem = findInventoryItem(item)
                
                return (
                  <SupplyCard
                    key={item.inputId}
                    inputId={item.inputId}
                    inputName={item.inputName}
                    category={item.category as InputCategory}
                    unit={item.unit}
                    currentStock={item.currentStock}
                    lotContext={{
                      requiredQuantity: item.requiredQuantity,
                      needToBuy: item.needToBuy,
                      stockStatus: item.stockStatus,
                      tasks: item.tasks,
                    }}
                    costPerUnit={inventoryItem?.lastPurchasePrice}
                    onClick={() => handleSupplyClick(item)}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}
      
      {/* AGC: Add Stock Modal */}
      <AddStockModal
        isOpen={isAddStockModalOpen}
        onClose={() => setIsAddStockModalOpen(false)}
        currentLotId={lotId}
        currentLotName={lotName}
        bomItems={bom.items}
        allLots={allLots}
        onConfirmEntry={handleStockEntry}
      />
      
      {/* AGC: Scan Invoice Modal (Coming Soon) */}
      <ScanInvoiceModal
        isOpen={isScanInvoiceModalOpen}
        onClose={() => setIsScanInvoiceModalOpen(false)}
      />
      
      {/* AGC: Supply Detail Modal - shows movements and prices */}
      {selectedSupply && (
        <SupplyDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false)
            setSelectedSupply(null)
          }}
          inputId={selectedSupply.inputId}
          inputName={selectedSupply.inputName}
          category={selectedSupply.category as InputCategory}
          unit={selectedSupply.unit}
          currentStock={selectedSupply.currentStock}
          inventoryItemId={findInventoryItem(selectedSupply)?.id}
          minimumStock={findInventoryItem(selectedSupply)?.minimumStock}
          lotContext={{
            requiredQuantity: selectedSupply.requiredQuantity,
            needToBuy: selectedSupply.needToBuy,
            stockStatus: selectedSupply.stockStatus,
            tasks: selectedSupply.tasks,
          }}
          onStockUpdated={calculateBOM}
        />
      )}
    </div>
  )
}

export default LotBOMCard
